import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useServices } from '../hooks/useServices';
import { contact } from '../config/contact';
import { trackCtaClick } from '../lib/analytics';
import { ServiceCard } from '../components/ServiceCard';
import { FeaturedWork } from '../portfolio/components/FeaturedWork';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/SEO';
import { ProcessSection } from '../components/ProcessSection';
import { OrganizationSchema, WebSiteSchema } from '../components/StructuredData';
import type { AudienceType } from '../types/api';
import { Users, GraduationCap, Building2, ArrowRight, Sparkles } from 'lucide-react';

const AUDIENCES: { value: AudienceType; label: string }[] = [
  { value: 'individual', label: 'home:audience.individual.title' },
  { value: 'student', label: 'home:audience.student.title' },
  { value: 'organization', label: 'home:audience.organization.title' },
];

export function HomePage() {
  const { t } = useTranslation(['home', 'common']);
  const { data: services, isLoading, error } = useServices();
  const [selectedAudience, setSelectedAudience] = useState<AudienceType | null>(null);

  const filteredServices = services?.filter((service) => {
    if (!selectedAudience) return true;
    return service.audience_types.includes(selectedAudience);
  });

  return (
    <div className="min-h-screen bg-dark">
      <SEO
        title="หน้าหลัก"
        description="HappyTech รับทำเว็บ แอป และระบบให้ธุรกิจไทย ดูแลครบจนเปิดใช้งานได้จริง"
        url="https://www.happytech.dev"
        image="https://www.happytech.dev/og-image.jpg"
      />
      
      <OrganizationSchema
        name="HappyTech"
        url="https://www.happytech.dev"
        logo="https://www.happytech.dev/og-image.jpg"
        description="HappyTech พัฒนาเว็บ แอป และระบบให้ธุรกิจไทย พร้อมให้คำปรึกษาด้านเทคนิคและดูแลหลังเปิดใช้งาน"
        sameAs={contact.sameAs}
      />
      
      <WebSiteSchema
        name="HappyTech"
        url="https://www.happytech.dev"
        description="พัฒนาเว็บและแอปมือถือ คำปรึกษาด้านเทคนิค โซลูชันดิจิทัล"
      />
      
      <Header />

      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          {/* Animated background orbs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="orb absolute top-20 left-1/4 w-96 h-96 bg-neon-cyan/10 rounded-full blur-3xl" />
            <div className="orb-delayed absolute bottom-20 right-1/4 w-96 h-96 bg-neon-purple/10 rounded-full blur-3xl" />
            <div className="orb absolute top-1/2 left-1/2 w-64 h-64 bg-neon-purple/5 rounded-full blur-2xl" style={{ animationDelay: '-10s' }} />
          </div>

          {/* Grid pattern overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">

            <div className="text-center max-w-3xl mx-auto">
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-2 px-4 py-2 glass rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-neon-cyan" />
                <span className="text-sm font-medium text-neon-cyan">{t('eyebrow')}</span>
              </div>

              {/* Heading with gradient text */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 font-heading">
                <span className="gradient-text">{t('hero.title')}</span>
              </h1>

              {/* Subheading */}
              <p className="text-lg sm:text-xl text-text-secondary max-w-2xl mx-auto leading-relaxed">
                {t('hero.subtitle')}
              </p>

{/* Audience quick-select cards */}
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                <Link
                  to="/audience/individual"
                  onClick={() => trackCtaClick('home_audience_individual')}
                  className="group flex flex-col items-center gap-3 p-6 glass-card rounded-2xl hover:bg-white/[0.07] hover:border-neon-cyan/30 hover:shadow-glow-cyan transition-all duration-300 cursor-pointer focus-ring"
                >
                  <div className="w-12 h-12 rounded-xl bg-neon-cyan/10 flex items-center justify-center group-hover:bg-neon-cyan/20 transition-colors">
                    <Users className="w-6 h-6 text-neon-cyan" />
                  </div>
                  <div className="text-center">
                    <h3 className="font-semibold text-text-primary font-heading group-hover:text-neon-cyan transition-colors">{t('audience.individual.title')}</h3>
                    <p className="text-sm text-text-secondary mt-1">{t('audience.individual.description')}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-text-muted group-hover:text-neon-cyan transition-colors" />
                </Link>

                <Link
                  to="/audience/student"
                  onClick={() => trackCtaClick('home_audience_student')}
                  className="group flex flex-col items-center gap-3 p-6 glass-card rounded-2xl hover:bg-white/[0.07] hover:border-neon-purple/30 hover:shadow-glow-purple transition-all duration-300 cursor-pointer focus-ring"
                >
                  <div className="w-12 h-12 rounded-xl bg-neon-purple/10 flex items-center justify-center group-hover:bg-neon-purple/20 transition-colors">
                    <GraduationCap className="w-6 h-6 text-neon-purple" />
                  </div>
                  <div className="text-center">
                    <h3 className="font-semibold text-text-primary font-heading group-hover:text-neon-purple transition-colors">{t('audience.student.title')}</h3>
                    <p className="text-sm text-text-secondary mt-1">{t('audience.student.description')}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-text-muted group-hover:text-neon-purple transition-colors" />
                </Link>

                <Link
                  to="/audience/organization"
                  onClick={() => trackCtaClick('home_audience_organization')}
                  className="group flex flex-col items-center gap-3 p-6 glass-card rounded-2xl hover:bg-white/[0.07] hover:border-neon-green/30 transition-all duration-300 cursor-pointer focus-ring"
                >
                  <div className="w-12 h-12 rounded-xl bg-neon-green/10 flex items-center justify-center group-hover:bg-neon-green/20 transition-colors">
                    <Building2 className="w-6 h-6 text-neon-green" />
                  </div>
                  <div className="text-center">
                    <h3 className="font-semibold text-text-primary font-heading group-hover:text-neon-green transition-colors">{t('audience.organization.title')}</h3>
                    <p className="text-sm text-text-secondary mt-1">{t('audience.organization.description')}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-text-muted group-hover:text-neon-green transition-colors" />
                </Link>
              </div>
            </div>
          </div>


        </section>

        {/* How We Work / Process */}
        <ProcessSection />

        {/* Services Section */}
        <section className="py-24 sm:py-32 relative">
          {/* Subtle background gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neon-purple/5 to-transparent pointer-events-none" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Section header with filters */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-text-primary font-heading">
                  {t('features.title')}
                </h2>
                <p className="mt-2 text-text-secondary">
                  {t('features.subtitle')}
                </p>
              </div>

              {/* Audience filters */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedAudience(null)}
                  className={`px-4 py-2 text-sm font-medium rounded-xl transition-all duration-300 cursor-pointer focus-ring ${selectedAudience === null
                    ? 'bg-gradient-to-r from-neon-cyan to-neon-purple text-white shadow-glow-cyan'
                    : 'glass-card text-text-secondary hover:text-neon-cyan hover:border-neon-cyan/30'
                    }`}
                >
                  {t('common:nav.services')}
                </button>
                {AUDIENCES.map((audience) => (
                  <button
                    key={audience.value}
                    onClick={() => setSelectedAudience(selectedAudience === audience.value ? null : audience.value)}
                    className={`px-4 py-2 text-sm font-medium rounded-xl transition-all duration-300 cursor-pointer focus-ring ${selectedAudience === audience.value
                      ? 'bg-gradient-to-r from-neon-cyan to-neon-purple text-white shadow-glow-cyan'
                      : 'glass-card text-text-secondary hover:text-neon-cyan hover:border-neon-cyan/30'
                      }`}
                  >
                    {t(audience.label)}
                  </button>
                ))}
              </div>
            </div>

            {/* Loading state */}
            {isLoading && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-48 glass-card rounded-2xl animate-pulse" />
                ))}
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="text-center py-12">
                <p className="text-neon-cyan mb-4">{t('errors.loadFailed')}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="text-neon-cyan hover:text-neon-purple font-medium cursor-pointer transition-colors duration-200 focus-ring rounded-md px-2 py-1"
                >
                  {t('errors.tryAgain')}
                </button>
              </div>
            )}

            {/* Services grid (Bento Box style) */}
            {filteredServices && filteredServices.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-[minmax(250px,auto)]">
                {filteredServices.map((service, index) => {
                  // Make the first item span 2 columns and 2 rows for a hero effect
                  const isFeatured = index === 0;
                  // Make every 4th item span 2 columns
                  const isWide = index % 4 === 3;

                  return (
                    <div
                      key={service.id}
                      className={`
                        ${isFeatured ? 'md:col-span-2 md:row-span-2' : ''}
                        ${isWide && !isFeatured ? 'md:col-span-2' : ''}
                      `}
                    >
                      <ServiceCard service={service} isFeatured={isFeatured} />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Empty state */}
            {filteredServices && filteredServices.length === 0 && (
              <div className="text-center py-16">
                <p className="text-text-secondary text-lg">{t('errors.noServices')}</p>
                <button
                  onClick={() => setSelectedAudience(null)}
                  className="mt-4 text-neon-cyan hover:text-neon-purple font-medium cursor-pointer transition-colors duration-200 focus-ring rounded-md px-2 py-1"
                >
                  {t('errors.viewAll')}
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Featured Work / Portfolio */}
        <FeaturedWork />
      </main>

      <Footer />
    </div>
  );
}
